import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	CardBody,
	Page,
	Select,
	Table,
	type TableColumn,
	useToast,
} from "@theg1group/ui";
import { type ReactNode, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AddressLink } from "../components/AddressLink.tsx";
import { ChainBadge } from "../components/ChainBadge.tsx";
import { KpiTile } from "../components/KpiTile.tsx";
import { TraceProgress } from "../components/TraceProgress.tsx";
import { api } from "../lib/api.ts";
import { fmtDate, fmtNum, fmtUsd, isJobActive, jobStatusTone } from "../lib/format.ts";
import type { FrontierRow, Rows, TraceDetailData } from "../lib/types.ts";

const STATE_OPTIONS = [
	{ value: "", label: "All states" },
	{ value: "pending", label: "Pending" },
	{ value: "fetching", label: "Fetching" },
	{ value: "expanded", label: "Expanded" },
	{ value: "stopped", label: "Stopped" },
	{ value: "error", label: "Error" },
];

export function TraceDetail() {
	const { id = "" } = useParams();
	const qc = useQueryClient();
	const toast = useToast();
	const [state, setState] = useState("");
	const [depth, setDepth] = useState("");

	const detail = useQuery({
		queryKey: ["trace", id],
		queryFn: () => api.get<TraceDetailData>(`/api/traces/${id}`),
		enabled: !!id,
		refetchInterval: (q) => (q.state.data && isJobActive(q.state.data.job.status) ? 5_000 : false),
	});
	const active = !!detail.data && isJobActive(detail.data.job.status);

	const frontier = useQuery({
		queryKey: ["trace-frontier", id, state, depth],
		queryFn: () =>
			api.get<Rows<FrontierRow>>(
				`/api/traces/${id}/frontier?state=${encodeURIComponent(state)}&depth=${encodeURIComponent(depth)}`,
			),
		enabled: !!id,
		refetchInterval: active ? 5_000 : false,
	});

	const cancel = useMutation({
		mutationFn: () => api.post(`/api/traces/${id}/cancel`),
		onSuccess: () => {
			toast.addToast("success", "Cancel requested");
			qc.invalidateQueries({ queryKey: ["trace", id] });
			qc.invalidateQueries({ queryKey: ["traces"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const d = detail.data;
	const j = d?.job;

	const cols: TableColumn<FrontierRow>[] = [
		{ id: "depth", header: "Depth", accessor: (r) => r.depth },
		{ id: "chain", header: "Chain", accessor: (r) => <ChainBadge chain={r.chain} /> },
		{
			id: "address",
			header: "Address",
			accessor: (r) => (
				<AddressLink id={r.addressId} address={r.address} display={r.display} head={10} tail={6} />
			),
		},
		{
			id: "state",
			header: "State",
			accessor: (r) => <Badge variant={stateTone(r.state)}>{r.state}</Badge>,
		},
		{ id: "reason", header: "Stop reason", accessor: (r) => r.stopReason ?? "—" },
		{ id: "path", header: "Path USD", accessor: (r) => fmtUsd(r.pathUsd) },
		{ id: "pages", header: "Pages", accessor: (r) => r.pagesDone },
		{ id: "attempts", header: "Attempts", accessor: (r) => r.attempts },
		{
			id: "error",
			header: "Error",
			accessor: (r) =>
				r.error ? <span className="text-danger text-xs break-all">{r.error}</span> : "—",
		},
	];

	if (detail.isLoading) return <Page title="Trace">Loading…</Page>;
	if (!d || !j) return <Page title="Trace">Not found.</Page>;

	const depthOptions = [
		{ value: "", label: "All depths" },
		...d.byDepth.map((b) => ({ value: String(b.depth), label: `Depth ${b.depth} (${b.count})` })),
	];

	return (
		<Page
			title={
				<span className="inline-flex items-center gap-2">
					Trace <span className="font-mono text-base">{j.id.slice(0, 8)}…</span>
					<Badge variant={jobStatusTone(j.status)}>{j.status}</Badge>
				</span>
			}
			description={
				<span>
					{j.kind}
					{j.partnerId && (
						<>
							{" · "}
							<Link to={`/partners/${j.partnerId}`} className="text-primary hover:underline">
								{j.partnerName ?? j.partnerId}
							</Link>
						</>
					)}
					{" · created "}
					{fmtDate(j.createdAt)}
				</span>
			}
			actions={
				<div className="flex gap-2 items-center">
					{active && <span className="text-xs text-muted">auto-refresh 5s</span>}
					<Button
						variant="danger"
						disabled={!active}
						loading={cancel.isPending}
						onClick={() => cancel.mutate()}
					>
						Cancel
					</Button>
				</div>
			}
		>
			<div
				className="grid gap-3.5 mb-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}
			>
				<KpiTile label="Visited" value={fmtNum(j.nodesVisited)} />
				<KpiTile label="Enqueued" value={fmtNum(j.nodesEnqueued)} />
				<KpiTile label="Provider calls" value={fmtNum(j.providerCalls)} />
				<KpiTile
					label="Budget"
					value={j.budgetHit ? "hit" : "ok"}
					tone={j.budgetHit ? "warning" : "success"}
					hint={`${fmtNum(j.params?.budgetNodes)} nodes`}
				/>
				<KpiTile label="Started" value={fmtDate(j.startedAt)} />
				<KpiTile
					label="Finished"
					value={fmtDate(j.finishedAt)}
					hint={j.lastTickAt ? `last tick ${fmtDate(j.lastTickAt)}` : undefined}
				/>
			</div>

			{j.error && (
				<Card className="mb-3.5">
					<CardBody>
						<div className="text-[11px] uppercase text-danger mb-1">Error</div>
						<div className="text-sm text-content font-mono break-all">{j.error}</div>
					</CardBody>
				</Card>
			)}

			<div
				className="grid gap-3.5 mb-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
			>
				<Panel title="Progress">
					<TraceProgress counts={d.progress} />
				</Panel>
				<Panel title="Params">
					<dl className="text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
						<KV k="depth" v={String(j.params?.depth ?? "—")} />
						<KV k="fanout" v={String(j.params?.fanout ?? "—")} />
						<KV k="budget" v={String(j.params?.budgetNodes ?? "—")} />
						<KV k="minUsd" v={fmtUsd(j.params?.minUsd)} />
						<KV k="direction" v={j.params?.direction ?? "—"} />
						<KV k="from" v={j.params?.from ? fmtDate(j.params.from, false) : "—"} />
						<KV k="to" v={j.params?.to ? fmtDate(j.params.to, false) : "—"} />
						{j.params?.seedAddressIds?.length ? (
							<KV k="seeds" v={String(j.params.seedAddressIds.length)} />
						) : null}
					</dl>
				</Panel>
				<Panel title="By depth">
					{d.byDepth.map((b) => (
						<Row
							key={b.depth}
							label={`depth ${b.depth}`}
							value={b.count}
							max={Math.max(...d.byDepth.map((x) => x.count), 1)}
						/>
					))}
					{!d.byDepth.length && <div className="text-xs text-muted">—</div>}
				</Panel>
				<Panel title="Stop reasons">
					{d.stopReasons.map((s) => (
						<Row
							key={s.reason}
							label={s.reason}
							value={s.count}
							max={Math.max(...d.stopReasons.map((x) => x.count), 1)}
						/>
					))}
					{!d.stopReasons.length && <div className="text-xs text-muted">—</div>}
				</Panel>
			</div>

			<Card>
				<CardBody>
					<div className="flex flex-wrap items-center justify-between gap-2 mb-3">
						<h2 className="text-sm font-semibold text-content">Frontier</h2>
						<div className="flex gap-2">
							<div className="w-40">
								<Select
									value={state}
									onChange={(e) => setState(e.target.value)}
									options={STATE_OPTIONS}
									size="sm"
								/>
							</div>
							<div className="w-44">
								<Select
									value={depth}
									onChange={(e) => setDepth(e.target.value)}
									options={depthOptions}
									size="sm"
								/>
							</div>
						</div>
					</div>
					<div className="overflow-x-auto">
						<Table
							columns={cols}
							data={frontier.data?.rows ?? []}
							keyExtractor={(r) => r.addressId}
							loading={frontier.isLoading}
							emptyMessage="Frontier empty."
						/>
					</div>
				</CardBody>
			</Card>
		</Page>
	);
}

function stateTone(state: string): "default" | "success" | "warning" | "error" | "info" {
	switch (state) {
		case "expanded":
			return "success";
		case "fetching":
			return "info";
		case "stopped":
			return "warning";
		case "error":
			return "error";
		default:
			return "default";
	}
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
	return (
		<Card>
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-2">{title}</h2>
				{children}
			</CardBody>
		</Card>
	);
}

function KV({ k, v }: { k: string; v: string }) {
	return (
		<>
			<dt className="text-muted">{k}</dt>
			<dd className="text-content">{v}</dd>
		</>
	);
}

function Row({ label, value, max }: { label: string; value: number; max: number }) {
	return (
		<div className="mb-1.5">
			<div className="flex justify-between text-xs text-content mb-0.5">
				<span className="truncate mr-2">{label}</span>
				<span className="text-muted">{fmtNum(value)}</span>
			</div>
			<div className="h-1.5 rounded-pill bg-surface-2 overflow-hidden">
				<div className="h-full bg-primary" style={{ width: `${(value / max) * 100}%` }} />
			</div>
		</div>
	);
}
